import React, { useEffect, useState } from "react";
import { BiPlus } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import Toggle from "../components/Toggle";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import Loading from "../components/Loading";

const API_URL = process.env.REACT_APP_API_URL;
const IMG_URL = process.env.REACT_APP_IMG_URL;

const PatchNotes = ({ dashboard = false }) => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [patch, setPatch] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/${language}/patchnotes`, {
        headers: { 
            Authorization: `Bearer ${
                JSON.parse(localStorage.getItem("user"))?.token
            }` 
        }
      })
      .then((res) => res.json())
      .then((data) => setPatch(data))
      .catch((err) => console.error(err));
  }, [language]);

  const handleToggle = async (patch) => {
    try {
      const res = await fetch(
        `${API_URL}/${language}/updatepatch/${patch._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${
              JSON.parse(localStorage.getItem("user")).token
            }`,
          },
          body: JSON.stringify({ ...patch, isPublished: !patch.isPublished }),
        }
      );

      if (res.ok) {
        setPatch((prev) =>
          prev.map((p) =>
            p._id === patch._id ? { ...p, isPublished: !patch.isPublished } : p
          )
        );
        toast.success(
          !patch.isPublished ? "Yama yayına alındı" : "Yama yayından kaldırıldı"
        );
      } else {
        const reply = await res.json();
        toast.error("Yama yayına alınamadı: " + reply.error);
      }
    } catch (err) {
      toast.error("Bir hata oluştu: ", err);
    }
  };

  const formattedDate = (date) =>
    new Date(date).toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });


  if (!patch) return <Loading />;

  
  return (
    <div className="mb-12">
      <h1 className="text-3xl sm:text-5xl font-bold my-12">
        {t("mobile2patchnotes")}
      </h1>

      <div
        className="mt-12 my-48 md:my-0 cursor-pointer hidden md:block"
        onClick={() =>
          navigate(
            `/patch/${
              patch
                .filter((p) => (!dashboard ? p.isPublished : true))
                .reverse()[0].yamaVersiyonu
            }`
          )
        }
      >
        <div className="md:flex md:flex-row flex-col w-full h-64 md:border-b-2 over">
          <div className="relative flex items-center justify-center md:w-7/12 h-full bg-black md:[clip-path:polygon(0_0,90%_0,70%_100%,0%_100%)]">
            <video
              autoPlay
              loop
              muted
              className="pr-0 md:pr-28 lg:pr-48 xl:pr-72 w-full h-full object-cover"
            >
              <source src="../assets/logo-deneme.mp4" type="video/mp4" />
            </video>
          </div>

          <div className="flex flex-col md:items-start items-center text-white md:-ml-12 lg:-ml-20 xl:-ml-28 md:border-t-2 justify-center">
            <h2 className="sm:text-4xl my-3 md:mb-6 md:mt-0 font-bold mx-2 text-3xl">
              {t("newupdatewaspublished")}
            </h2>
            <div className="flex flex-col text-lg text-start gap-2 w-96 mx-12 px-12">
              {patch && (
                <>
                  <p>
                    {
                      patch.filter((p) => p.isPublished)[
                        patch.filter((p) => p.isPublished).length - 1
                      ]?.title
                    }
                  </p>
                  <p>
                    {
                      patch.filter((p) => p.isPublished)[
                        patch.filter((p) => p.isPublished).length - 1
                      ]?.subtitle
                    }
                  </p>
                  <p className="underline text-end mt-4 inline-block transition-transform duration-300 ease-out hover:translate-x-4">
                    {t("details")}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <motion.div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 mx-2 sm:p-16 gap-12 gap-y-12 sm:gap-y-36">
        {dashboard && (
          <div
            className="border-2 rounded-lg cursor-pointer col-span-1 lg:col-span-2 xl:col-span-3 -mb-12 sm:-mb-28"
            onClick={() => navigate(`/dashboard/add-patch`)}
          >
            <div className="rounded-t-lg p-2">{t("addnewpatch")}</div>
            <div className="relative w-100 h-48 col-span-2 overflow-hidden rounded-b-lg shadow-lg items-center justify-center">
              <h2 className="absolute inset-0 flex items-center justify-center text-2xl gap-4 font-bold">
                <BiPlus className="mt-1" />
                {t("addnewpatch")}
              </h2>
            </div>
          </div>
        )}

        {patch
          .filter((p) => (!dashboard ? p.isPublished : true))
          .slice()
          .reverse()
          .map((patch, i) => (
            <motion.div
              key={i}
              className="flex flex-col mt-12"
              initial={{ opacity: 0, scale: 0.96, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 20 }}
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 30,
                delay: i * 0.3,
              }}
              style={{ transformOrigin: "center top" }}
            >
              <div
                className="card bg-transparent shadow-sm grid 2xl:mt-0 xl:gap-y-6 2xl:grid-cols-2 xl:grid-cols-1 md:grid-cols-2 cursor-pointer"
                onClick={() =>
                  dashboard
                    ? navigate(`/dashboard/update-patch/${patch.yamaVersiyonu}`)
                    : navigate(`/patch/${patch.yamaVersiyonu}`)
                }
              >
                <div className="rounded-2xl shadow-xl text-6xl w-full items-center flex justify-center font-bold text-gray-300 overflow-hidden transition-transform duration-300 ease-in-out transform hover:scale-105">
                  <img
                    className="rounded-2xl h-48 w-full transition-transform duration-300 ease-in-out transform hover:scale-125"
                    src={IMG_URL + patch.image}
                    alt={patch.title}
                  />
                </div>

                <div className=" p-0 m-0 mx-4 mt-8 md:mt-0 flex flex-col">
                  <div className="text-start">
                    <h2 className="card-title mb-6 text-md line-clamp-2 break-all">
                      {patch.title}
                    </h2>
                    <p className="line-clamp-2 mb-6">{patch.summary}</p>
                  </div>
                  <div className="flex justify-between h-full">
                    <h1 className="text-sm self-end font-semibold">
                      {formattedDate(patch.tarih)}
                    </h1>
                    {dashboard ? (
                      <button className="underline self-end border-white inline-block transition-transform duration-300 ease-out hover:translate-x-4">
                        {t("details")}
                      </button>
                    ) : (
                      <button className="underline self-end border-white inline-block transition-transform duration-300 ease-out hover:translate-x-4">
                        {t("details")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {dashboard && (
                <div className="flex">
                  <div className="flex h-full mt-2 md:w-1/2 justify-center items-end">
                    <Toggle
                      disabled={patch?.isPublished}
                      onChange={() => handleToggle(patch)}
                    />
                  </div>
                  <button
                    onClick={() => navigate(`/patch/${patch.yamaVersiyonu}`)}
                    className="right-0 ml-auto mr-2 btn btn-success h-full mt-3 rounded-full justify-self-end cursor-pointer"
                  >
                    Önizle
                  </button>
                </div>
              )}
            </motion.div>
          ))}
      </motion.div>
    </div>
  );
};

export default PatchNotes;
